import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { thousandSeparator } from '../../../utils/helpers';
import { useYear } from '../../../hooks/useYear';
import { IndicatorsTableProps } from './types';
import useStyles from './styles';

const IndicatorsTable: React.FC<IndicatorsTableProps> = ({
  columns,
  rows,
  currencySymbol = '$',
  thousandSeparatorChar = '.',
  rightColumnLabel,
}) => {
  const classes = useStyles();
  const { selectedYear } = useYear();
  const showVariationUnderAmount = selectedYear > 2026;

  const formatAmount = (value?: number) => {
    if (value === undefined || value === null) {
      return '';
    }

    return `${currencySymbol} ${thousandSeparator(
      Number.isNaN(value) ? 0 : value,
      thousandSeparatorChar,
    )}`;
  };

  const formatPercentage = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return '';
    }

    const numericValue = Number(value);
    const prefix = numericValue > 0 ? '+' : '';
    return `${prefix}${numericValue.toFixed(1)}%`;
  };

  const formatPlainPercentage = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return '';
    }

    const numericValue = Number(value);
    return `${numericValue.toFixed(2)}%`;
  };

  const getVariationClassName = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return `${classes.variation} ${classes.neutral}`;
    }

    if (value > 0) {
      return `${classes.variation} ${classes.positive}`;
    }

    if (value < 0) {
      return `${classes.variation} ${classes.negative}`;
    }

    return `${classes.variation} ${classes.neutral}`;
  };

  return (
    <TableContainer component={Paper} className={classes.root}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell} />
            {columns.map((column) => (
              <TableCell key={column.id} className={classes.headerCell}>
                {column.label}
              </TableCell>
            ))}
            {rightColumnLabel && (
              <TableCell className={classes.rightHeaderCell}>
                {rightColumnLabel}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              className={row.id === 'totals' ? classes.totalsRow : undefined}
            >
              <TableCell className={classes.labelCell}>
                {row.label}
              </TableCell>
              {columns.map((column) => {
                const cell = row.values[column.id];
                const isTotalColumn = column.id === 'total' || column.highlightBackground;
                const isDebtPercentageColumn = column.id === 'debtPercentage';

                if (typeof cell === 'number') {
                  return (
                    <TableCell key={column.id} className={classes.dataCell}>
                      {cell}%
                    </TableCell>
                  );
                }

                return (
                  <TableCell
                    key={column.id}
                    className={
                      isTotalColumn
                        ? `${classes.dataCell} ${classes.totalColumnCell}`
                        : classes.dataCell
                    }
                  >
                    <Typography className={classes.amount}>
                      {isDebtPercentageColumn
                        ? formatPlainPercentage(cell?.amount)
                        : formatAmount(cell?.amount)}
                    </Typography>
                    {showVariationUnderAmount && (
                      <Typography
                        className={
                          isDebtPercentageColumn
                            ? (() => {
                              const value = cell?.variation;
                              if (value === undefined || value === null || Number.isNaN(value)) {
                                return `${classes.variation} ${classes.neutral}`;
                              }
                              if (value < 0) {
                                return `${classes.variation} ${classes.positive}`;
                              }
                              if (value > 0) {
                                return `${classes.variation} ${classes.negative}`;
                              }
                              return `${classes.variation} ${classes.neutral}`;
                            })()
                            : getVariationClassName(cell?.variation)
                        }
                      >
                        {formatPercentage(cell?.variation)}
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
              {rightColumnLabel && (
                <TableCell className={classes.rightCell}>
                  {row.rightValue !== undefined && row.rightValue !== null
                    ? `${row.rightValue.toFixed(2)}%`
                    : '-'}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default IndicatorsTable;
